import React, { useEffect, useState } from 'react';
import { IconButton } from '@mui/material';
import {
  Facebook as FacebookIcon,
  Instagram as InstagramIcon,
  LinkedIn as LinkedInIcon,
  YouTube as YouTubeIcon,
} from '@mui/icons-material';
import { FaTiktok } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import NewsletterForm from '../NewsletterForm';
import api from '../../services/api';
import { categoryHref } from '../../utils/categoryLinks';
import { FeaturedCategory } from '../../utils/featuredCategories';
import { useSiteInfo } from '../../hooks/useSiteInfo';
import { brandName, footerCompanyParts } from '../../utils/companyInfo';
import './shell.css';

interface FooterLink {
  label: string;
  to?: string;
  href?: string;
}

/** A lábléc kategória-linkjeinek száma (a kiemelt kategóriákból). */
export const FOOTER_CATEGORY_LIMIT = 4;

/** A „Kategóriák” oszlop: a valóban létező kiemelt kategóriák (adatvezérelt — friss
 *  telepítésen, kategóriák nélkül csak a céges ajánlat látszik) + céges ajándék. */
export const categoryColumnLinks = (categories: FeaturedCategory[]): FooterLink[] => [
  ...categories.slice(0, FOOTER_CATEGORY_LIMIT).map((c) => ({ label: c.name, to: categoryHref(c.slug) })),
  { label: 'Céges ajándék', to: '/ceges-ajanlat' },
];

const staticColumns: { title: string; links: FooterLink[] }[] = [
  {
    title: 'Segítség',
    links: [
      { label: 'Rendeléskövetés', to: '/rendeles-kovetese' },
      { label: 'Gyors rendelés', to: '/gyors-rendeles' },
      { label: 'Céges ajánlatkérés', to: '/ceges-ajanlat' },
      { label: 'GYIK', to: '/gyik' },
      { label: 'Kapcsolat', to: '/kapcsolat' },
    ],
  },
  {
    title: 'Rólunk',
    links: [
      { label: 'A műhely', to: '/rolunk' },
      { label: 'Ügyfélszolgálat', to: '/ugyfelszolgalat' },
      { label: 'Visszaküldés', to: '/visszakuldesi-szabalyzat' },
      { label: 'Szállítási módok', to: '/szallitasi-modok' },
      { label: 'ÁSZF', to: '/aszf' },
    ],
  },
];

const paymentMethods = ['Bankkártya', 'Utánvét', 'Átutalás', 'Apple Pay', 'Google Pay'];

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  // Cégadatok a Webshop beállításokból — csak a kitöltött mezők jelennek meg.
  const { data: site } = useSiteInfo();
  const companyParts = footerCompanyParts(site);

  const [featured, setFeatured] = useState<FeaturedCategory[]>([]);
  useEffect(() => {
    let active = true;
    api
      .get<FeaturedCategory[]>('/categories/featured/', { params: { limit: FOOTER_CATEGORY_LIMIT } })
      .then((res) => {
        if (active) setFeatured(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);
  const linkColumns = [{ title: 'Kategóriák', links: categoryColumnLinks(featured) }, ...staticColumns];

  const socialLinks = [
    { url: process.env.REACT_APP_FACEBOOK_URL, label: 'Facebook', icon: <FacebookIcon /> },
    { url: process.env.REACT_APP_INSTAGRAM_URL, label: 'Instagram', icon: <InstagramIcon /> },
    // A react-icons komponense ReactNode-ot ad vissza (TS 4.9 JSX-ként nem fogadja el); tiszta
    // függvény, hook nélkül — elemként hívjuk.
    { url: process.env.REACT_APP_TIKTOK_URL, label: 'TikTok', icon: FaTiktok({ style: { fontSize: 22 } }) },
    { url: process.env.REACT_APP_YOUTUBE_URL, label: 'YouTube', icon: <YouTubeIcon /> },
    { url: process.env.REACT_APP_LINKEDIN_URL, label: 'LinkedIn', icon: <LinkedInIcon /> },
  ].filter((s) => Boolean(s.url));

  const cpyLinkStyle: React.CSSProperties = {
    color: 'inherit',
    textDecoration: 'none',
  };

  return (
    <div className="gt5shell">
      <footer>
        <div className="w">
          <div className="fg">
            {/* Brand column */}
            <div>
              <RouterLink className="br" to="/">
                <img src="/brand/logo-mark-light.svg" alt="GT Kreatív Sarok" />
                <div>
                  <b>Kreatív Sarok</b>
                  <span>Egyedi nyomat</span>
                </div>
              </RouterLink>

              <p style={{ margin: '12px 0 0', fontSize: '13.5px', color: '#A8A49B', maxWidth: '34ch' }}>
                Egyedi nyomott ajándékok egy darabtól, országos kiszállítással. Magyar családi vállalkozás.
              </p>

              {/* Newsletter (preserved) */}
              <div style={{ marginTop: 20, maxWidth: 360 }}>
                <h5>Hírlevél</h5>
                <NewsletterForm variant="compact" source="footer" />
              </div>

              {/* Payment method chips */}
              <div className="pays">
                {paymentMethods.map((method) => (
                  <span key={method}>{method}</span>
                ))}
              </div>

              {/* Social links (env-gated, preserved) */}
              {socialLinks.length > 0 && (
                <div style={{ display: 'flex', gap: 4, marginTop: 14, marginLeft: -8 }}>
                  {socialLinks.map((social) => (
                    <IconButton
                      key={social.label}
                      component="a"
                      href={social.url as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      sx={{ color: '#C9C6BE', '&:hover': { color: '#F2A93B' } }}
                    >
                      {social.icon}
                    </IconButton>
                  ))}
                </div>
              )}
            </div>

            {/* Link columns */}
            {linkColumns.map((column) => (
              <div key={column.title}>
                <h5>{column.title}</h5>
                <ul>
                  {column.links.map((link) => (
                    <li key={link.label}>
                      {link.to ? (
                        <RouterLink to={link.to}>{link.label}</RouterLink>
                      ) : (
                        <a href={link.href}>{link.label}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {companyParts.length > 0 && (
            <div className="cpy" style={{ borderTop: 'none', paddingBottom: 0, flexWrap: 'wrap' }}>
              <span>{companyParts.join(' · ')}</span>
            </div>
          )}

          {/* Bottom copyright bar */}
          <div className="cpy">
            <span>© {currentYear} {brandName(site)} — minden jog fenntartva</span>
            <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <RouterLink to="/adatvedelem" style={cpyLinkStyle}>
                Adatkezelés
              </RouterLink>
              <span aria-hidden>·</span>
              <button
                type="button"
                onClick={() => {
                  localStorage.removeItem('cookieConsent');
                  window.location.reload();
                }}
                style={{
                  ...cpyLinkStyle,
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  font: 'inherit',
                }}
              >
                Cookie-beállítások
              </button>
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Footer;
